import { flatten, get, isEmpty, set, startCase, uniq, unset } from 'lodash';

import COMPLEXITY_VALUES from '~/server_shared/generated-types/COMPLEXITY_VALUES';
import { getObjectPaths } from '~/Utils';
import { isLocaleRegionIsUs } from '~/Utils/regionUtils';

const getCategoryIdsUsedInSubTypes = (values) => {
  const categories = [];
  const incidentTypes = get(values, 'configuration.incident_details.types', []);
  incidentTypes.forEach((type) => {
    const subTypes = get(type, 'sub_types', []);
    subTypes.forEach((subType) => {
      if (subType.category) {
        categories.push(subType.category);
      }
    });
  });

  return categories;
};

const getCategoryIdsUsedFields = (values) => {
  const categories = [];
  const categoriesPaths = getObjectPaths(values)?.filter((path) => path.includes('.incident_sub_type_categories'));

  categoriesPaths.forEach((path) => {
    const fieldCategories = get(values, path, []);
    if (!isEmpty(fieldCategories)) {
      categories.push(fieldCategories);
    }
  });

  return uniq(flatten(categories));
};

const migrateCategoriesOnTheFly = (values, policyCategoryKeys) => {
  const categoriesSection = get(values, 'configuration.categories', []);
  const categoriesIds = categoriesSection?.map((category) => category.id);
  const categoriesUsedInSubTypes = getCategoryIdsUsedInSubTypes(values);
  const categoriesUsedFields = getCategoryIdsUsedFields(values);
  const categoriesIdsInUsed = uniq(flatten([...categoriesUsedFields, ...categoriesUsedInSubTypes]));

  // A limit list holds one flat key namespace: incident categories live in this blob,
  // policy categories in their own table. Fabricating a blob entry for a policy key would
  // duplicate it into both dropdown groups and persist it as a phantom incident category
  // on the next save.
  const missingCategories = categoriesIdsInUsed
    .filter((categoryId) => !categoriesIds.includes(categoryId) && !policyCategoryKeys.includes(categoryId))
    .map((categoryId) => ({ id: categoryId, desc: startCase(categoryId) }));

  // Appended to a copy: `categoriesSection` is the array the screen fetched, so a back-fill
  // pushed into it would outlive this run.
  const categories = isEmpty(missingCategories) ? categoriesSection : [...categoriesSection, ...missingCategories];
  return { ...values, configuration: { ...values.configuration, categories } };
};

const staticWitnessesFields = {
  contact_ref_id: {
    id: 'contact_ref_id',
    desc: 'Witness',
    type: 'contact',
    active: true,
    mandatory: false,
  },
  description: {
    id: 'description',
    desc: 'Description',
    type: 'string',
    active: true,
    mandatory: false,
  },
};

const staticTriageFields = {
  active: false,
  complexity: {
    desc: 'Initial Complexity',
    type: 'select',
    active: false,
    mandatory: false,
    options: Object.entries(COMPLEXITY_VALUES).map(([key, value]) => ({
      id: key,
      desc: value,
      active: true,
    })),
  },
};

const staticExternalClaimNumberDefaults = {
  active: false,
};

const addStaticFields = (values) => {
  Object.keys(staticWitnessesFields).forEach((fieldId) => {
    if (!get(values, `configuration.witnesses.${fieldId}`)) {
      set(values, `configuration.witnesses.${fieldId}`, staticWitnessesFields[fieldId]);
    }
  });

  Object.keys(staticTriageFields).forEach((fieldId) => {
    if (!get(values, `configuration.triage.${fieldId}`)) {
      set(values, `configuration.triage.${fieldId}`, staticTriageFields[fieldId]);
    }
  });

  return values;
};

const ICD_CODE_CONFIG = {
  icd_codes: {
    type: 'pre_defined_fields',
    desc: 'Icd Codes',
    geo_specific: 'US',
    active: true,
    mandatory: false,
    dynamic: true,
  },
};

const INSURED_FIELDS_CONFIG = {
  insurer_contact_id: {
    type: 'contact',
    desc: 'Adverse Carrier',
    active: false,
    mandatory: false,
    accepted_roles: ['insurer'],
  },
  insurer_reference_number: {
    type: 'string',
    desc: 'Adverse Carrier Reference Number',
    active: false,
    mandatory: false,
  },
  insurer_policy_number: {
    type: 'string',
    desc: 'Adverse Carrier Policy Number',
    active: false,
    mandatory: false,
  },
};

const migrateIcdCodesFields = (values) => {
  const isUsRegion = isLocaleRegionIsUs();
  if (!isUsRegion) {
    return values;
  }

  const INVOLVED_PARTIES = 'configuration.involved_parties';
  const autoInvolvedToMigrate = ['involved_person', 'involved_driver', 'involved_passenger', 'involved_non_motorist'];
  const involvedPartiesKeys = Object.keys(get(values, INVOLVED_PARTIES, {}));

  if (values.lob === 'auto_claim') {
    autoInvolvedToMigrate.forEach((partyKey) => {
      for (const fieldKey in ICD_CODE_CONFIG) {
        if (!get(values, `${INVOLVED_PARTIES}.${partyKey}.${fieldKey}`)) {
          set(values, `${INVOLVED_PARTIES}.${partyKey}.${fieldKey}`, ICD_CODE_CONFIG[fieldKey]);
        }
      }
    });

    return values;
  }

  involvedPartiesKeys.forEach((partyKey) => {
    for (const fieldKey in ICD_CODE_CONFIG) {
      const bodilyInjurySection = get(values, `${INVOLVED_PARTIES}.${partyKey}.bodily_injury_loss`);
      if (bodilyInjurySection && !get(values, `${INVOLVED_PARTIES}.${partyKey}.bodily_injury_loss.${fieldKey}`)) {
        set(values, `${INVOLVED_PARTIES}.${partyKey}.bodily_injury_loss.${fieldKey}`, ICD_CODE_CONFIG[fieldKey]);
      }
    }
  });

  return values;
};

const migrateInvolvedInsuredFields = (values) => {
  const INVOLVED_PARTIES = 'configuration.involved_parties';
  const involvedPartiesKeys = Object.keys(get(values, INVOLVED_PARTIES, {}));

  involvedPartiesKeys.forEach((partyKey) => {
    for (const fieldKey in INSURED_FIELDS_CONFIG) {
      if (!get(values, `${INVOLVED_PARTIES}.${partyKey}.${fieldKey}`)) {
        set(values, `${INVOLVED_PARTIES}.${partyKey}.${fieldKey}`, INSURED_FIELDS_CONFIG[fieldKey]);
      }
    }
  });

  return values;
};

const migrateNewFields = (values) => {
  values = migrateInvolvedInsuredFields(values);
  values = migrateIcdCodesFields(values);
  return values;
};

const migrateInheritAutoInvolvedFields = (values) => {
  if (!values.lob === 'auto_claim') {
    return values;
  }

  const involvedParties = get(values, 'configuration.involved_parties', {});
  const involvedProperty = get(values, 'configuration.involved_parties.involved_property', {});
  const involvedPerson = get(values, 'configuration.involved_parties.involved_person', {});

  Object.keys(involvedParties).forEach((partyKey) => {
    const involvedEntity = involvedParties[partyKey];
    if (involvedEntity?.inherit === 'involved_property') {
      Object.keys(involvedProperty).forEach((fieldKey) => {
        if (!get(involvedEntity, fieldKey)) {
          set(values, `configuration.involved_parties.${partyKey}.${fieldKey}`, involvedProperty[fieldKey]);
        }
      });
    }
    if (involvedEntity?.inherit === 'involved_person') {
      Object.keys(involvedPerson).forEach((fieldKey) => {
        if (!get(involvedEntity, fieldKey)) {
          set(values, `configuration.involved_parties.${partyKey}.${fieldKey}`, involvedPerson[fieldKey]);
        }
      });
    }
  });

  return values;
};

const addOrUpdateExternalClaimNumberConfig = (values) => {
  const basePath = 'configuration.external_claim_numbers';

  if (get(values, basePath) === undefined) {
    set(values, basePath, { ...staticExternalClaimNumberDefaults });
  } else {
    if (get(values, `${basePath}.active`) === undefined) {
      set(values, `${basePath}.active`, staticExternalClaimNumberDefaults.active);
    }
  }
  return values;
};

export const removeStaticFields = (values) => {
  Object.keys(staticWitnessesFields).forEach((fieldId) => {
    if (get(values, `configuration.witnesses.${fieldId}`)) {
      unset(values, `configuration.witnesses.${fieldId}`);
    }
  });
  return values;
};

// Custom Additional Information sections are gated on the CUSTOM_FNOL_ADDITIONAL_INFORMATION_SECTIONS
// feature flag: seed the empty list, on every LOB, only when the flag is on, so a config never gains the
// key while the feature is off.
const migrateAdditionalInformationSections = (values, isCustomSectionsEnabled) => {
  if (!isCustomSectionsEnabled) {
    return values;
  }
  const path = 'configuration.incident_details.additional_information_sections';
  // Coerce a missing OR explicitly-null list to []: the field is nullable, so a stored JSON `null` must
  // normalize here too, or the downstream `.map` on the raw value blanks the config screen.
  const current = get(values, path);
  if (current === undefined || current === null) {
    set(values, path, []);
  }
  return values;
};

export const setOnTheFlyFnolConfigurationMigrations = (
  values,
  isCustomSectionsEnabled = false,
  policyCategoryKeys = []
) => {
  values = addStaticFields(values);
  values = migrateInheritAutoInvolvedFields(values);
  values = migrateNewFields(values);
  values = addOrUpdateExternalClaimNumberConfig(values);
  values = migrateAdditionalInformationSections(values, isCustomSectionsEnabled);
  return migrateCategoriesOnTheFly(values, policyCategoryKeys);
};

export default setOnTheFlyFnolConfigurationMigrations;
